/**
 * Great-circle distance helpers, shared by every surface that measures a
 * point-to-point distance in miles.
 *
 * Extracted as pure functions (no Angular / Google Maps dependency) so the
 * distance math can be unit-tested directly — the classic risks here are unit
 * mix-ups (km vs miles) and the GeoJSON `[lng, lat]` vs `{lat, lng}` swap, both
 * cheap to guard once and assert against known city pairs.
 */

/** Mean Earth radius in miles — every distance here is in miles, not km. */
const EARTH_RADIUS_MILES = 3958.7613;

export interface LatLng {
  lat: number;
  lng: number;
}

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/**
 * Haversine great-circle distance between two points, in **miles**.
 *
 * Returns `NaN` when either point is null/undefined or carries a non-finite
 * lat/lng — callers treat a `NaN` result as "not within radius" rather than
 * crashing on a missing geoPoint.
 */
export function haversineMiles(a: LatLng | null | undefined, b: LatLng | null | undefined): number {
  if (!a || !b) {
    return NaN;
  }
  if (
    !Number.isFinite(a.lat) ||
    !Number.isFinite(a.lng) ||
    !Number.isFinite(b.lat) ||
    !Number.isFinite(b.lng)
  ) {
    return NaN;
  }

  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);

  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

  return 2 * EARTH_RADIUS_MILES * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * True when `point` is within `radiusMiles` of `center` (inclusive). A null
 * point, a null center, or a non-positive radius yields `false`.
 */
export function isWithinMiles(
  point: LatLng | null | undefined,
  center: LatLng | null | undefined,
  radiusMiles: number,
): boolean {
  if (!(radiusMiles > 0)) {
    return false;
  }
  const distance = haversineMiles(point, center);
  return Number.isFinite(distance) && distance <= radiusMiles;
}
